/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useMemo } from 'react';
import { parse } from 'yaml';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { MCP_CLIENT_IDS, type McpClientId } from '../mcpConnect/types';

/** Same rules the backend enforces (lib/mcpEntry.ts); here only for instant feedback. */
export const NAME_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const PLAIN_HTTPS_URL = /^https:\/\/[A-Za-z0-9.-]+(:[0-9]{1,5})?(\/[A-Za-z0-9._~%/-]*)?$/;
export const LOCAL_COMMAND = /^[a-z0-9][a-z0-9._-]*$/;
export const LOCAL_ARG = /^[A-Za-z0-9._@=:/+-]+$/;

export type EntryFields = {
  title: string;
  description: string;
  provider: string;
  url: string;
  docsUrl: string;
  tags: string[];
  lifecycle: 'production' | 'experimental';
  owner: string;
  /** Remote: `url`. Local (PLAT-2691): the client runs `command args…` over stdio. */
  transport: 'remote' | 'local';
  command: string;
  args: string[];
};

export const EMPTY_FIELDS: EntryFields = {
  title: '', description: '', provider: '', url: '', docsUrl: '', tags: [], lifecycle: 'production', owner: 'm42-infra',
  transport: 'remote', command: '', args: [],
};

/** The backend keeps the remote field shape when transport is omitted, so a
 * remote proposal goes out exactly as it did before local entries existed. */
export const proposalFields = (fields: EntryFields) => {
  const { transport, command, args, ...remote } = fields;
  return transport === 'local' ? { ...remote, url: '', transport, command, args } : remote;
};

export type ProposalInput = {
  mode: 'add' | 'edit';
  name: string;
  fields: ReturnType<typeof proposalFields>;
  testedClients: McpClientId[];
  attestedClients: McpClientId[];
  baseSha?: string;
};

export type PendingProposal = {
  number: number;
  title: string;
  url: string;
  author: string;
  createdAt: string;
  name: string;
  mode: 'add' | 'update';
  status?: 'ok' | 'unavailable';
  checks?: { state: 'none' | 'running' | 'failing' | 'passing'; passed: number; total: number };
  review?: 'approved' | 'changes_requested' | 'awaiting';
};

/** Rebuild server-provided PR URLs from validated GitHub URL parts. */
export const githubPr = (prUrl: string): { repo: string; href: string } | undefined => {
  try {
    const url = new URL(prUrl);
    const match = url.pathname.match(/^\/([^/]+)\/([^/]+)\/pull\/(\d+)\/?$/);
    if (url.protocol !== 'https:' || url.hostname !== 'github.com' || !match) return undefined;
    const [, owner, repo, number] = match;
    return {
      repo,
      href: `https://github.com/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/pull/${Number(number)}`,
    };
  } catch {
    return undefined;
  }
};

export type ProposalErrorBody = { error: string; field?: string; message?: string; detail?: string; prNumber?: number; prUrl?: string };

export class ProposalError extends Error {
  constructor(readonly status: number, readonly body: ProposalErrorBody) {
    super(body.message ?? body.error);
    this.name = 'ProposalError';
  }
}

export function fieldsFromYaml(raw: string): { name: string; fields: EntryFields; testedClients: McpClientId[] } {
  const parsed: unknown = parse(raw);
  const e = (parsed !== null && typeof parsed === 'object' ? parsed : {}) as {
    metadata?: { name?: string; title?: string; description?: string; tags?: string[]; links?: Array<{ url?: string }>; annotations?: Record<string, string> };
    spec?: { lifecycle?: string; owner?: string; remotes?: Array<{ url?: string }>; local?: { command?: unknown; args?: unknown } };
  };
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const a = e.metadata?.annotations ?? {};
  const tags = e.metadata?.tags;
  const known = MCP_CLIENT_IDS as readonly string[];
  const local = e.spec?.local && typeof e.spec.local === 'object' ? e.spec.local : undefined;
  return {
    name: str(e.metadata?.name),
    fields: {
      title: str(e.metadata?.title),
      description: str(e.metadata?.description),
      provider: str(a['m42.io/mcp-provider']),
      url: str(e.spec?.remotes?.[0]?.url),
      docsUrl: str(e.metadata?.links?.[0]?.url),
      tags: Array.isArray(tags) ? tags.filter((t): t is string => typeof t === 'string') : [],
      lifecycle: e.spec?.lifecycle === 'experimental' ? 'experimental' : 'production',
      owner: str(e.spec?.owner) || EMPTY_FIELDS.owner,
      transport: local ? 'local' : 'remote',
      command: str(local?.command),
      args: Array.isArray(local?.args) ? local.args.filter((arg): arg is string => typeof arg === 'string') : [],
    },
    testedClients: str(a['m42.io/mcp-clients'])
      .split(',').map(c => c.trim()).filter((c): c is McpClientId => known.includes(c)),
  };
}

export function useProposalsApi() {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  return useMemo(() => {
    const call = async <T>(path: string, init?: RequestInit): Promise<T> => {
      const base = await discovery.getBaseUrl('mcp-catalog');
      const res = await fetchApi.fetch(`${base}${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
      });
      const body = await res.json().catch(() => ({ error: 'http', message: res.statusText }));
      if (!res.ok) throw new ProposalError(res.status, body as ProposalErrorBody);
      return body as T;
    };
    return {
      /** null when the feature is off (404), so the page hides the buttons. */
      capabilities: async () => {
        try {
          return await call<{ canPropose: boolean }>('/capabilities');
        } catch (e) {
          if (e instanceof ProposalError && (e.status === 404 || e.status === 401)) return null;
          throw e;
        }
      },
      getEntry: (name: string) => call<{ raw: string; sha: string; pendingPr?: { number: number; url: string } | null }>(`/entries/${encodeURIComponent(name)}`),
      pending: () => call<{ items: PendingProposal[] }>('/pending'),
      preview: (input: ProposalInput) =>
        call<{ files: Array<{ path: string; content: string }> }>('/preview', { method: 'POST', body: JSON.stringify(input) }),
      submit: (input: ProposalInput) =>
        call<{ status: 'created' | 'updated'; prUrl: string; prNumber: number }>('/proposals', { method: 'POST', body: JSON.stringify(input) }),
    };
  }, [discovery, fetchApi]);
}
